import { useTraduccion } from '@tallerya/i18n';
import {
  Boton,
  CampoEtiquetas,
  CampoInterruptor,
  CampoNumero,
  CampoSegmentado,
  CampoSeleccion,
  CampoTexto,
  Fila,
  Formulario,
  Seccion,
  useAvisos,
  useFormulario,
  type EstadoFormulario,
} from '@tallerya/ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { DesactivarItem } from './DesactivarItem';
import { encabezado } from './encabezado';
import {
  numeroDe,
  pedirSiguienteCodigo,
  useCambiosItem,
  useCategorias,
  useFichaItem,
  useSiguienteCodigo,
  version,
  type Clase,
  type FichaItem,
  type ValoresItem,
} from './datos';
import { VentanaCategoria } from './VentanaCategoria';

// Alta, edición y duplicado de un producto o servicio (#113): «Agregar
// servicio», «Agregar producto», «Editar servicio» y sus estados en el Figma.
// La misma pantalla para las tres, como en clientes:
//
//   /productos-servicios/nuevo                 alta, con el código propuesto
//   /productos-servicios/nuevo?duplicar=:id    alta prellenada con otro ítem
//   /productos-servicios/:id/editar            edición, con la versión (#77)
//
// Lo propio del catálogo:
//
//   - el código propuesto **cambia con el tipo** —`SRV-` o `PRD-` (#110)—,
//     mientras nadie lo haya tocado;
//   - la duración se escribe en horas y viaja en minutos, y en un producto ni
//     se muestra: los productos no la llevan;
//   - el combo de categoría trae «Agregar categoría» al final, que abre la
//     ventana y deja elegida la que se crea (#108).

const RUTA = '/productos-servicios';

const ALTA: ValoresItem = {
  kind: 'service',
  code: '',
  name: '',
  categoryId: '',
  listPrice: '',
  horas: '',
  externalCode: '',
  aliases: [],
  isActive: true,
};

export function FormularioItem() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const duplicar = params.get('duplicar') ?? undefined;
  const alta = id === undefined;
  const ficha = useFichaItem(id ?? duplicar);
  // El tipo del que se duplica decide el prefijo; sin duplicar, el alta arranca en servicio.
  const clase: Clase = ficha.data?.kind ?? ALTA.kind;
  const siguiente = useSiguienteCodigo(clase, alta && (!duplicar || ficha.isSuccess));
  useTituloPantalla(alta ? t('admin.catalogo.nuevo') : t('admin.catalogo.editar.titulo'));

  // El error antes que la espera: al duplicar, la propuesta solo se pide con la
  // ficha en la mano, así que con una ficha fallida quedaría esperando para
  // siempre y este aviso no saldría nunca.
  if ((id ?? duplicar) !== undefined && (ficha.isError || ficha.data === null)) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.catalogo.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.catalogo.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(RUTA)}>
          {t('admin.catalogo.volver')}
        </Boton>
      </section>
    );
  }

  const cargando = (alta ? siguiente.isPending : false) || ((id ?? duplicar) !== undefined && ficha.isPending);
  if (cargando) return <p className="entrada">{t('app.cargando')}</p>;

  if (alta) {
    // Duplicar: todo lo del original menos el código —uno nuevo de su tipo— y
    // el estado, que nace activo. La propuesta se espera antes de dibujar, para
    // no escribirla encima de lo que la persona ya tecleó.
    const base = ficha.data ? { ...valoresDeFicha(ficha.data), isActive: true } : ALTA;
    return (
      <Editor
        key={duplicar ?? 'nuevo'}
        inicial={{ ...base, code: siguiente.data ?? '' }}
        codigoPropuesto={siguiente.data ?? ''}
      />
    );
  }

  // `ficha.data` está, por las dos salidas de arriba. La clave lleva la versión:
  // si la ficha cambia en la base y se vuelve a pedir, el formulario arranca de
  // nuevo con los valores frescos.
  const datos = ficha.data as FichaItem;
  return <Editor key={`${datos.id}-${datos.version}`} ficha={datos} inicial={valoresDeFicha(datos)} />;
}

interface PropsEditor {
  inicial: ValoresItem;
  ficha?: FichaItem;
  /** Solo el alta: el código que se mostró ya puesto, o vacío si no se pudo saber. */
  codigoPropuesto?: string;
}

function Editor({ inicial, ficha, codigoPropuesto = '' }: PropsEditor) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  // Desde el listado se vuelve con «Atrás», que conserva sus filtros en la URL.
  // Quien entró por un enlace no tiene nada detrás y va al listado limpio.
  const origen = useLocation().state as { desdeListado?: boolean } | null;
  const volver = () => void (origen?.desdeListado ? navegar(-1) : navegar(RUTA));
  const { avisar } = useAvisos();
  const cambios = useCambiosItem();
  const categorias = useCategorias();
  const [propuesto, setPropuesto] = useState(codigoPropuesto);
  const [creandoCategoria, setCreandoCategoria] = useState(false);
  const [desactivando, setDesactivando] = useState(false);
  const editando = ficha !== undefined;

  const form = useFormulario<ValoresItem>({
    inicial,
    // La cortesía de no ir y volver por lo obvio. La que vale es la de la API.
    validar: (v) => ({
      ...(!v.name.trim() && {
        name: v.kind === 'service' ? 'catalogo.nombre_servicio' : 'catalogo.nombre_producto',
      }),
      ...(editando && !v.code.trim() && { code: 'validation.required' }),
      ...precioInvalido(v.listPrice),
      ...(v.kind === 'service' && duracionInvalida(v.horas)),
    }),
    guardar: (v) =>
      ficha ? cambios.editar(String(ficha.id), version(ficha.version), v) : cambios.crear(v, propuesto),
    // Los 409 y 400 que son de un campo: se marcan bajo él y no arriba.
    camposPorCodigo: {
      'catalog_item.codigo_tomado': 'code',
      'catalog_item.nombre_duplicado': 'name',
      'catalog_item.categoria_no_disponible': 'categoryId',
      'catalog_item.duracion_solo_servicios': 'horas',
    },
  });

  // Si la persona ya escribió su código, ninguna propuesta lo pisa. Y de las
  // propuestas pedidas solo vale la última: con Servicio → Producto → Servicio
  // rápido, un `PRD-` que llega tarde no puede quedar en un servicio. Son refs y
  // no estado porque solo se leen en la respuesta, no al dibujar.
  const codigoTocado = useRef(false);
  const ultimoPedido = useRef(0);

  function proponer(clase: Clase) {
    const pedido = ++ultimoPedido.current;
    void pedirSiguienteCodigo(clase).then((codigo) => {
      if (pedido !== ultimoPedido.current || codigoTocado.current) return;
      setPropuesto(codigo);
      form.cambiar('code', codigo);
    });
  }

  // Dos cambios que arrastran otro, en el momento y no en un efecto que
  // corregiría el estado después de dibujarlo:
  //   - el código: escribirlo es tocarlo, y desde ahí manda la persona;
  //   - el tipo, en el alta: se pide el código del tipo nuevo —`SRV-` pasa a
  //     `PRD-`—, salvo que la persona ya haya escrito el suyo.
  const formConReglas: EstadoFormulario<ValoresItem> = {
    ...form,
    cambiar: (campo, valor) => {
      if (campo === 'code') codigoTocado.current = true;
      form.cambiar(campo, valor);
      if (campo === 'kind' && !editando && !codigoTocado.current) proponer(valor as Clase);
    },
  };

  const opcionesCategoria = (categorias.data ?? []).map((c) => ({ valor: String(c.id), etiqueta: c.name }));
  // La categoría que ya tenía el ítem aunque después se haya desactivado: sin
  // esto, el combo diría «Sin categoría» y el ítem la tiene. La lista solo trae
  // las activas, y la ficha trae el id pero no el nombre; por eso se nombra así.
  // Como en `FormularioCliente` con el tipo de documento.
  if (
    form.valores.categoryId &&
    categorias.data &&
    !opcionesCategoria.some((o) => o.valor === form.valores.categoryId)
  ) {
    opcionesCategoria.push({ valor: form.valores.categoryId, etiqueta: t('admin.catalogo.formulario.categoriaInactiva') });
  }

  function guardado(siguiente: 'volver' | 'otro') {
    avisar({
      titulo: t('admin.catalogo.aviso.guardado', { nombre: form.valores.name.trim() }),
      texto: t('admin.catalogo.aviso.guardadoTexto'),
    });
    if (siguiente === 'otro') {
      // Se limpia antes de pedir el código: con los datos del recién creado a la
      // vista, un segundo «Guardar» en la espera daría de alta un duplicado. El
      // tipo se conserva: quien carga varios servicios seguidos no lo cambia.
      const clase = form.valores.kind;
      form.reiniciar({ ...ALTA, kind: clase });
      setPropuesto('');
      codigoTocado.current = false;
      requestAnimationFrame(() => document.getElementById(form.idDe('name'))?.focus());
      proponer(clase);
      return;
    }
    volver();
  }

  const servicio = form.valores.kind === 'service';

  return (
    <>
      <Formulario
        form={form}
        titulo={
          editando
            ? servicio
              ? t('admin.catalogo.editar.titulo')
              : t('admin.catalogo.editar.tituloProducto')
            : t('admin.catalogo.nuevo')
        }
        info={editando ? t('admin.catalogo.editar.info') : t('admin.catalogo.formulario.entrada')}
        {...(ficha && { descripcion: encabezado(ficha, idioma, t) })}
        {...(editando && { etiquetaGuardar: t('admin.catalogo.formulario.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={guardado}
        onCancelar={volver}
        {...(ficha?.isActive && {
          accionSecundaria: (
            <Boton variante="peligro" onClick={() => setDesactivando(true)}>
              {t('admin.catalogo.formulario.desactivar')}
            </Boton>
          ),
        })}
      >
        {/* El orden es el del Figma: el tipo solo, arriba, porque decide lo demás;
            código, nombre y categoría en una fila; y el código externo con el precio. */}
        <Seccion titulo={t('admin.catalogo.formulario.general')}>
          {/* El tipo no se cambia al editar: el código, la duración y la llave de
              nombre dependen de él (#110). */}
          {!editando && (
            <CampoSegmentado
              form={formConReglas}
              nombre="kind"
              etiqueta={t('admin.catalogo.formulario.tipo')}
              obligatorio
              opciones={[
                { valor: 'service', etiqueta: t('admin.catalogo.servicio') },
                { valor: 'product', etiqueta: t('admin.catalogo.producto') },
              ]}
            />
          )}
          <Fila>
            <CampoTexto
              form={formConReglas}
              nombre="code"
              etiqueta={t('admin.catalogo.formulario.codigo')}
              // En el alta, si no se pudo proponer un código, vacío vale: lo asigna la API.
              obligatorio={editando || propuesto !== ''}
              info={
                editando
                  ? t('admin.catalogo.formulario.codigoAvisoEdicion')
                  : t('admin.catalogo.formulario.codigoAyuda')
              }
              autoComplete="off"
            />
            <CampoTexto
              form={form}
              nombre="name"
              etiqueta={t('admin.catalogo.formulario.nombre')}
              obligatorio
              ancho="largo"
              autoComplete="off"
            />
            <CampoSeleccion
              form={formConReglas}
              nombre="categoryId"
              etiqueta={t('admin.catalogo.formulario.categoria')}
              placeholder={t('admin.catalogo.formulario.sinCategoria')}
              opciones={opcionesCategoria}
              // Al pie de la lista, como en el Figma: abre la ventana, y al crearla queda elegida.
              accion={{
                etiqueta: t('admin.catalogo.formulario.agregarCategoria'),
                onSeleccionar: () => setCreandoCategoria(true),
              }}
            />
          </Fila>
        </Seccion>

        <Seccion
          titulo={t('admin.catalogo.formulario.precioYTiempo')}
          info={t('admin.catalogo.formulario.precioYTiempoAyuda')}
        >
          <Fila>
            {/* Seis decimales, los de la columna (`numeric(18,6)`). */}
            <CampoNumero
              form={form}
              nombre="listPrice"
              etiqueta={t('admin.catalogo.formulario.precio')}
              {...(editando && { info: t('admin.catalogo.formulario.precioAvisoEdicion') })}
              decimales={6}
              prefijo="$"
            />
            {/* Los productos no llevan duración: se entregan, no se trabajan. En
                horas con dos decimales, como se muestra al editar: «1.25» son 75 minutos. */}
            {servicio && (
              <CampoNumero
                form={form}
                nombre="horas"
                etiqueta={t('admin.catalogo.formulario.duracion')}
                decimales={2}
                sufijo={t('admin.catalogo.formulario.horas')}
              />
            )}
            <CampoTexto
              form={form}
              nombre="externalCode"
              etiqueta={t('admin.catalogo.formulario.codigoExterno')}
              info={t('admin.catalogo.formulario.codigoExternoAyuda')}
              autoComplete="off"
            />
          </Fila>
        </Seccion>

        <Seccion titulo={t('admin.catalogo.formulario.voz')} info={t('admin.catalogo.formulario.vozAyuda')}>
          <CampoEtiquetas
            form={form}
            nombre="aliases"
            etiqueta={t('admin.catalogo.formulario.otrosNombres')}
            placeholder={t('admin.catalogo.formulario.otrosNombresPlaceholder')}
            etiquetaQuitar={t('admin.catalogo.formulario.quitar')}
            ancho="largo"
          />
        </Seccion>

        <Seccion titulo={t('admin.catalogo.formulario.estado')}>
          <CampoInterruptor
            form={form}
            nombre="isActive"
            etiqueta={t('admin.catalogo.activo')}
            info={t('admin.catalogo.formulario.activoAyuda')}
          />
        </Seccion>
      </Formulario>

      <VentanaCategoria
        abierta={creandoCategoria}
        onCerrar={() => setCreandoCategoria(false)}
        onCreada={(categoria) => form.cambiar('categoryId', String(categoria.id))}
      />

      <DesactivarItem
        ficha={desactivando ? (ficha ?? null) : null}
        onCerrar={() => setDesactivando(false)}
        onDesactivado={() => {
          avisar({ titulo: t('admin.catalogo.aviso.desactivado', { nombre: ficha?.name ?? '' }) });
          volver();
        }}
      />
    </>
  );
}

/** La ficha como valores del formulario: la duración pasa de minutos a horas. */
function valoresDeFicha(ficha: FichaItem): ValoresItem {
  const minutos = ficha.referenceDurationMinutes === null ? null : Number(ficha.referenceDurationMinutes);
  return {
    kind: ficha.kind,
    code: ficha.code,
    name: ficha.name,
    categoryId: ficha.categoryId ? String(ficha.categoryId) : '',
    listPrice: ficha.listPrice === null ? '' : String(Number(ficha.listPrice)),
    horas: minutos ? String(Math.round((minutos / 60) * 100) / 100) : '',
    externalCode: ficha.externalCode ?? '',
    aliases: ficha.aliases,
    isActive: ficha.isActive,
  };
}

/**
 * El precio: un número, sin negativos y dentro de lo que cabe en la columna
 * (`numeric(18,6)`, ver `CatalogItemRules.PrecioMaximo`). Lo de afuera la API lo
 * rechaza igual, pero con un error que no llega a este campo.
 */
function precioInvalido(texto: string): { listPrice?: string } {
  const precio = numeroDe(texto);
  if (precio === null) return {};
  if (Number.isNaN(precio)) return { listPrice: 'catalogo.numero_invalido' };
  return precio > 999_999_999_999 ? { listPrice: 'validation.too_large' } : {};
}

/**
 * La duración, en horas: mayor que cero —la API exige minutos positivos— y que
 * quepa en los minutos que guarda la columna (`smallint`, hasta 32767: unas 546
 * horas). La API responde por `referenceDurationMinutes`, que no es este campo:
 * sin revisarlo acá, el error no se marcaría bajo él.
 */
function duracionInvalida(texto: string): { horas?: string } {
  const horas = numeroDe(texto);
  if (horas === null) return {};
  if (Number.isNaN(horas)) return { horas: 'catalogo.numero_invalido' };
  const minutos = Math.round(horas * 60);
  if (minutos <= 0) return { horas: 'validation.too_small' };
  return minutos > 32_767 ? { horas: 'validation.too_large' } : {};
}
